import { useState } from "react";

import { useUser } from "@components/user-context";
import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import { type GetCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import {
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";

import { MenuItem } from "./menu-item";
import { MenuSubmenuItem } from "./menu-submenu-item";

interface AttributionModelSelectorProps {
  compare: Compare;
  updateCompare: (compare: Compare) => void;
  getCompareQueryMetadata: GetCompareQueryMetadata;
  isDisabled?: boolean;
}

export const AttributionModelSelector = ({
  compare,
  updateCompare,
  getCompareQueryMetadata,
  isDisabled = false,
}: AttributionModelSelectorProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const { user } = useUser();
  const amEnum = user.attributionMethodEnum;
  const compareQueryMetadata = getCompareQueryMetadata({
    queryKey: "attributionModel",
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isHovering,
    onOpenChange: setIsHovering,
    transform: false,
    placement: "right-start",
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div>
      <MenuItem
        isDisabled={isDisabled}
        isHovering={isHovering}
        icon={<span className="mr-3">{compareQueryMetadata.icon}</span>}
        text={compareQueryMetadata.label}
        setReference={refs.setReference}
        {...getReferenceProps()}
      />
      {isHovering && (
        <div
          className="absolute rounded-md bg-white elevation-1 py-1 z-10 text-sm w-56"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {amEnum.reactSelectOptions.map((option) => (
            <MenuSubmenuItem
              key={option.value}
              label={option.label}
              isSelected={
                compare.queryKey === "attributionModel" &&
                compare.queryValue === option.value
              }
              onClick={() =>
                updateCompare({
                  ...compare,
                  queryKey: "attributionModel",
                  queryValue: option.value,
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};
